import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from '@/features/auth/AuthContext'
import { supabase, Store, StoreUser } from '@/lib/supabase/client'
import { toast } from 'sonner'

interface StoreContextType {
  stores: Store[]
  currentStore: Store | null
  storeUsers: StoreUser[]
  loading: boolean
  switchStore: (storeId: string) => void
  refreshStores: () => Promise<void>
  createStore: (name: string) => Promise<{ error: Error | null }>
}

const StoreContext = createContext<StoreContextType | undefined>(undefined)

export function StoreProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [stores, setStores] = useState<Store[]>([])
  const [currentStore, setCurrentStore] = useState<Store | null>(null)
  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch user's stores
  const fetchStores = async () => {
    if (!user) {
      setStores([])
      setCurrentStore(null)
      setLoading(false)
      return
    }

    try {
      // Get all stores user has access to
      const { data: storeUsersData, error: storeUsersError } = await supabase
        .from('store_users')
        .select('*, stores(*)')
        .eq('user_id', user.id)

      if (storeUsersError) throw storeUsersError

      const userStores = storeUsersData?.map((su: any) => su.stores).filter(Boolean) || []
      setStores(userStores)
      setStoreUsers(storeUsersData || [])

      // Set current store from localStorage or first store
      const savedStoreId = localStorage.getItem('current_store_id')
      const storeToSet = savedStoreId
        ? userStores.find((s: Store) => s.id === savedStoreId)
        : userStores[0]

      if (storeToSet) {
        setCurrentStore(storeToSet)
        localStorage.setItem('current_store_id', storeToSet.id)
      }
    } catch (error) {
      console.error('Error fetching stores:', error)
      toast.error('Failed to load stores')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStores()
  }, [user])

  const switchStore = (storeId: string) => {
    const store = stores.find((s) => s.id === storeId)
    if (store) {
      setCurrentStore(store)
      localStorage.setItem('current_store_id', storeId)
      toast.success(`Switched to ${store.name}`)
    }
  }

  const refreshStores = async () => {
    await fetchStores()
  }

  const createStore = async (name: string) => {
    if (!user) {
      return { error: new Error('User not authenticated') }
    }

    try {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-')

      const { data, error } = await supabase
        .from('stores')
        .insert({
          name,
          slug,
          owner_id: user.id,
        })
        .select()
        .single()

      if (error) throw error

      await refreshStores()
      toast.success(`Store "${name}" created!`)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  return (
    <StoreContext.Provider
      value={{
        stores,
        currentStore,
        storeUsers,
        loading,
        switchStore,
        refreshStores,
        createStore,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

export function useStore() {
  const context = useContext(StoreContext)
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider')
  }
  return context
}
